import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { UsersIcon, EyeIcon, ClockIcon, GlobeIcon } from 'lucide-react';
import { getEvents, VisitEvent } from '../../lib/analytics';
type Range = 'today' | '7d' | '30d' | '90d' | 'all';
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const RANGE_LABELS: Record<Range, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time'
};
function getRangeCutoff(range: Range): Date | null {
  if (range === 'all') return null;
  const d = new Date();
  if (range === 'today') {
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  d.setDate(d.getDate() - days);
  return d;
}
function StatCard({
  icon: Icon,
  label,
  value,
  sub
}: {
  icon: any;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return <div className="border border-border bg-card p-6 space-y-3" data-id="element-667">
      <div className="flex items-center gap-2 text-muted-foreground" data-id="element-668">
        <Icon className="w-4 h-4" data-id="element-669" />
        <span className="font-mono text-xs uppercase tracking-widest" data-id="element-670">
          {label}
        </span>
      </div>
      <div className="font-display text-3xl font-bold" data-id="element-671">{value}</div>
      {sub && <div className="font-mono text-xs text-muted-foreground" data-id="element-672">{sub}</div>}
    </div>;
}
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (!active || !payload?.length) return null;
  return <div className="bg-card border border-border px-3 py-2 font-mono text-xs" data-id="element-673">
      <div className="text-muted-foreground mb-1" data-id="element-674">{label}</div>
      <div className="text-primary font-bold" data-id="element-675">{payload[0].value} visits</div>
    </div>;
};
export function Analytics() {
  const [range, setRange] = useState<Range>('today');
  const [events, setEvents] = useState<VisitEvent[]>([]);
  const [syncing, setSyncing] = useState(true);
  // Load from Supabase on mount
  useEffect(() => {
    getEvents().then(data => {
      setEvents(data);
      setSyncing(false);
    });
  }, []);
  const filtered = useMemo(() => {
    const cutoff = getRangeCutoff(range);
    if (!cutoff) return events;
    return events.filter(e => new Date(e.timestamp) >= cutoff);
  }, [events, range]);
  // Summary stats
  const totalVisits = filtered.length;
  const uniqueSessions = new Set(filtered.map(e => e.sessionId)).size;
  const topPage = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach(e => {
      counts[e.page] = (counts[e.page] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || '—';
  }, [filtered]);
  const topSource = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach(e => {
      counts[e.source] = (counts[e.source] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || '—';
  }, [filtered]);
  // Visits over time (daily)
  const visitsByDay = useMemo(() => {
    const counts: Record<string, number> = {};
    const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 60;
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      counts[key] = 0;
    }
    filtered.forEach(e => {
      const key = new Date(e.timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      if (key in counts) counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([date, visits]) => ({
      date,
      visits
    }));
  }, [filtered, range]);
  // Visits by hour
  const visitsByHour = useMemo(() => {
    const counts = Array.from({
      length: 24
    }, (_, h) => ({
      hour: `${h}:00`,
      visits: 0
    }));
    filtered.forEach(e => {
      const h = new Date(e.timestamp).getHours();
      counts[h].visits++;
    });
    return counts;
  }, [filtered]);
  // Visits by day of week
  const visitsByDow = useMemo(() => {
    const counts = DAYS.map(day => ({
      day,
      visits: 0
    }));
    filtered.forEach(e => {
      const d = new Date(e.timestamp).getDay();
      counts[d].visits++;
    });
    return counts;
  }, [filtered]);
  // Traffic sources
  const sources = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach(e => {
      counts[e.source] = (counts[e.source] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([source, count]) => ({
      source,
      count,
      pct: totalVisits ? Math.round(count / totalVisits * 100) : 0
    }));
  }, [filtered, totalVisits]);
  // Top pages
  const pages = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach(e => {
      counts[e.page] = (counts[e.page] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([page, count]) => ({
      page,
      count,
      pct: totalVisits ? Math.round(count / totalVisits * 100) : 0
    }));
  }, [filtered, totalVisits]);
  return <div className="p-8 max-w-7xl mx-auto space-y-10 pb-24" data-id="element-676">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-677">
        <div data-id="element-678">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-679">
            Analytics
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-680">
            Visitor insights for your portfolio.
            {syncing && <span className="ml-2 text-primary animate-pulse" data-id="element-681">Syncing…</span>}
          </p>
        </div>
        {/* Range filter */}
        <div className="flex border border-border" data-id="element-682">
          {(Object.keys(RANGE_LABELS) as Range[]).map(r => <button key={r} onClick={() => setRange(r)} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${range === r ? 'bg-primary text-background' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'}`} data-id="element-683">
              {RANGE_LABELS[r].replace('Last ', '').replace(' days', 'd')}
            </button>)}
        </div>
      </div>

      {events.length === 0 ? <div className="border border-dashed border-border p-16 text-center" data-id="element-684">
          <p className="font-mono text-sm text-muted-foreground" data-id="element-685">
            No analytics data yet.
          </p>
          <p className="font-mono text-xs text-muted-foreground mt-2" data-id="element-686">
            Visit your portfolio to start tracking.
          </p>
        </div> : <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-687">
            <StatCard icon={EyeIcon} label="Total Visits" value={totalVisits} sub={RANGE_LABELS[range]} data-id="element-688" />
            <StatCard icon={UsersIcon} label="Unique Sessions" value={uniqueSessions} sub="Distinct visitors" data-id="element-689" />
            <StatCard icon={GlobeIcon} label="Top Source" value={topSource} sub="Most traffic from" data-id="element-690" />
            <StatCard icon={ClockIcon} label="Top Page" value={topPage} sub="Most visited" data-id="element-691" />
          </div>

          {/* Visits over time */}
          <div className="border border-border bg-card p-6 space-y-4" data-id="element-692">
            <h2 className="font-display text-lg font-bold" data-id="element-693">Visits Over Time</h2>
            <ResponsiveContainer width="100%" height={220} data-id="element-694">
              <LineChart data={visitsByDay} margin={{
            top: 4,
            right: 8,
            left: -20,
            bottom: 0
          }} data-id="element-695">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" data-id="element-696" />
                <XAxis dataKey="date" tick={{
              fontSize: 10,
              fontFamily: 'monospace',
              fill: 'hsl(var(--muted-foreground))'
            }} tickLine={false} axisLine={false} interval="preserveStartEnd" data-id="element-697" />
                <YAxis tick={{
              fontSize: 10,
              fontFamily: 'monospace',
              fill: 'hsl(var(--muted-foreground))'
            }} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-698" />
                <Tooltip content={<CustomTooltip data-id="element-700" />} data-id="element-699" />
                <Line type="monotone" dataKey="visits" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} activeDot={{
              r: 4,
              fill: 'hsl(var(--primary))'
            }} data-id="element-701" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Hour + Day of week */}
          <div className="grid md:grid-cols-2 gap-6" data-id="element-702">
            <div className="border border-border bg-card p-6 space-y-4" data-id="element-703">
              <h2 className="font-display text-lg font-bold" data-id="element-704">Visits by Hour</h2>
              <p className="font-mono text-xs text-muted-foreground" data-id="element-705">
                When are people visiting?
              </p>
              <ResponsiveContainer width="100%" height={180} data-id="element-706">
                <BarChart data={visitsByHour} margin={{
              top: 4,
              right: 8,
              left: -20,
              bottom: 0
            }} data-id="element-707">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} data-id="element-708" />
                  <XAxis dataKey="hour" tick={{
                fontSize: 9,
                fontFamily: 'monospace',
                fill: 'hsl(var(--muted-foreground))'
              }} tickLine={false} axisLine={false} interval={3} data-id="element-709" />
                  <YAxis tick={{
                fontSize: 9,
                fontFamily: 'monospace',
                fill: 'hsl(var(--muted-foreground))'
              }} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-710" />
                  <Tooltip content={<CustomTooltip data-id="element-712" />} data-id="element-711" />
                  <Bar dataKey="visits" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} opacity={0.85} data-id="element-713" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-border bg-card p-6 space-y-4" data-id="element-714">
              <h2 className="font-display text-lg font-bold" data-id="element-715">Visits by Day</h2>
              <p className="font-mono text-xs text-muted-foreground" data-id="element-716">
                Which days get the most traffic?
              </p>
              <ResponsiveContainer width="100%" height={180} data-id="element-717">
                <BarChart data={visitsByDow} margin={{
              top: 4,
              right: 8,
              left: -20,
              bottom: 0
            }} data-id="element-718">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} data-id="element-719" />
                  <XAxis dataKey="day" tick={{
                fontSize: 10,
                fontFamily: 'monospace',
                fill: 'hsl(var(--muted-foreground))'
              }} tickLine={false} axisLine={false} data-id="element-720" />
                  <YAxis tick={{
                fontSize: 10,
                fontFamily: 'monospace',
                fill: 'hsl(var(--muted-foreground))'
              }} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-721" />
                  <Tooltip content={<CustomTooltip data-id="element-723" />} data-id="element-722" />
                  <Bar dataKey="visits" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} opacity={0.85} data-id="element-724" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Sources + Pages */}
          <div className="grid md:grid-cols-2 gap-6" data-id="element-725">
            {/* Traffic sources */}
            <div className="border border-border bg-card p-6 space-y-4" data-id="element-726">
              <h2 className="font-display text-lg font-bold" data-id="element-727">
                Traffic Sources
              </h2>
              <p className="font-mono text-xs text-muted-foreground" data-id="element-728">
                Where visitors come from
              </p>
              <div className="space-y-2" data-id="element-729">
                {sources.length === 0 ? <p className="font-mono text-xs text-muted-foreground" data-id="element-730">
                    No data
                  </p> : sources.map(({
              source,
              count,
              pct
            }) => <div key={source} className="space-y-1" data-id="element-731">
                      <div className="flex items-center justify-between" data-id="element-732">
                        <span className="font-mono text-xs text-foreground" data-id="element-733">
                          {source}
                        </span>
                        <span className="font-mono text-xs text-muted-foreground" data-id="element-734">
                          {count} ({pct}%)
                        </span>
                      </div>
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden" data-id="element-735">
                        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{
                  width: `${pct}%`
                }} data-id="element-736" />
                      </div>
                    </div>)}
              </div>
            </div>

            {/* Top pages */}
            <div className="border border-border bg-card p-6 space-y-4" data-id="element-737">
              <h2 className="font-display text-lg font-bold" data-id="element-738">Top Pages</h2>
              <p className="font-mono text-xs text-muted-foreground" data-id="element-739">
                Most visited URLs
              </p>
              <div className="space-y-2" data-id="element-740">
                {pages.length === 0 ? <p className="font-mono text-xs text-muted-foreground" data-id="element-741">
                    No data
                  </p> : pages.map(({
              page,
              count,
              pct
            }) => <div key={page} className="space-y-1" data-id="element-742">
                      <div className="flex items-center justify-between gap-4" data-id="element-743">
                        <span className="font-mono text-xs text-foreground truncate" data-id="element-744">
                          {page}
                        </span>
                        <span className="font-mono text-xs text-muted-foreground shrink-0" data-id="element-745">
                          {count} ({pct}%)
                        </span>
                      </div>
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden" data-id="element-746">
                        <div className="h-full bg-primary/60 rounded-full transition-all duration-500" style={{
                  width: `${pct}%`
                }} data-id="element-747" />
                      </div>
                    </div>)}
              </div>
            </div>
          </div>

          {/* Recent visits */}
          <div className="border border-border bg-card" data-id="element-748">
            <div className="p-6 border-b border-border" data-id="element-749">
              <h2 className="font-display text-lg font-bold" data-id="element-750">Recent Visits</h2>
            </div>
            <div className="divide-y divide-border" data-id="element-751">
              {filtered.slice(0, 20).map(e => <div key={e.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-6 py-3 items-center text-xs font-mono" data-id="element-752">
                  <span className="text-foreground truncate" data-id="element-753">{e.page}</span>
                  <span className="text-muted-foreground" data-id="element-754">{e.source}</span>
                  <span className="text-muted-foreground hidden md:block" data-id="element-755">
                    {e.language}
                  </span>
                  <span className="text-muted-foreground whitespace-nowrap" data-id="element-756">
                    {new Date(e.timestamp).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </span>
                </div>)}
            </div>
          </div>
        </>}
    </div>;
}